// 読書合宿プラン画面
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, renderHistorySection, bindHistoryHandlers, renderBookFilter, getFilterState, applyBookFilter, bindFilterCounter } from '../ui.js';
import { fetchCourse, fetchAllNotesParallel } from '../api.js';
import { getAllBooks, upsertBooks, saveAiHistory, getAiHistoryByType } from '../cache.js';

async function ensureNotes(books) {
  const missing = books.filter((b) => !b.note).length;
  if (!missing) return;
  showLoading(`書籍メモを補完中… (0/${missing})`);
  await fetchAllNotesParallel(books, (done, total) => {
    const ov = document.getElementById('overlay-msg');
    if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
  });
  await upsertBooks(books);
  hideLoading();
}

export async function showReadingCourseScreen() {
  const books = await getAllBooks();
  await renderPicker(books);
}

async function renderPicker(books) {
  const histItems = await getAiHistoryByType('course');
  const html = `
    ${screenHead('読書合宿プラン')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      テーマと冊数を指定すると、AI が段階的な読書コースを設計します。既読書は自動で除外されます。
    </p>
    ${renderBookFilter()}
    <div class="form-group">
      <label class="form-label">テーマ</label>
      <textarea class="form-textarea" id="course-theme" rows="2" placeholder="例: 美学を体系的に学びたい / 写真と倫理の関係 / 戦後日本の知的系譜"></textarea>
    </div>
    <div class="form-group">
      <label class="form-label">レベル</label>
      <select class="form-select" id="course-level">
        <option value="入門">入門</option>
        <option value="中級" selected>中級</option>
        <option value="上級">上級</option>
      </select>
    </div>
    <div class="form-group">
      <label class="form-label">冊数</label>
      <select class="form-select" id="course-count">
        <option value="3">3 冊</option>
        <option value="4" selected>4 冊</option>
        <option value="5">5 冊</option>
      </select>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>コースを設計</span>
    </button>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('reading-course', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  bindFilterCounter(screen, books);
  screen.querySelector('#btn-go').addEventListener('click', async () => {
    const theme = screen.querySelector('#course-theme').value.trim();
    const level = screen.querySelector('#course-level').value;
    const count = parseInt(screen.querySelector('#course-count').value, 10);
    if (!theme) {
      toast('テーマを入力してください');
      return;
    }
    const filtered = applyBookFilter(books, getFilterState(screen));
    await runCourse(theme, level, count, filtered.length ? filtered : books);
  });
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => {
      const c = entry.context || {};
      renderResult(entry.payload, c.theme || '', c.level || '中級', c.count || 4, books);
    },
    () => renderPicker(books)
  );
}

async function runCourse(theme, level, count, books) {
  await ensureNotes(books);
  showLoading('読書コースを設計中…');
  try {
    const data = await fetchCourse(theme, level, count, books);
    await saveAiHistory({
      type: 'course',
      title: data.title || theme,
      subtitle: `${level} · ${count} 冊`,
      context: { theme, level, count },
      payload: data,
    });
    renderResult(data, theme, level, count, books);
  } catch (e) {
    toast('設計に失敗しました');
  } finally {
    hideLoading();
  }
}

export async function renderFromHistory(entry) {
  const books = await getAllBooks();
  const c = entry.context || {};
  renderResult(entry.payload, c.theme || '', c.level || '中級', c.count || 4, books);
}

function renderResult(data, theme, level, count, books) {
  const steps = data.steps || [];
  const totalWeeks = steps.reduce((s, x) => s + (Number(x.weeks) || 0), 0);
  const html = `
    ${screenHead('読書合宿プラン')}
    <p class="section-label">theme</p>
    <p style="margin:0 0 16px;font-family:var(--font-serif);font-size:16px;line-height:1.6;">${escapeHtml(theme)}</p>

    <h2 style="margin:24px 0 8px;font-family:var(--font-serif);font-size:22px;font-weight:500;">${escapeHtml(data.title || 'コース')}</h2>
    <p style="margin:0 0 8px;font-family:var(--font-mono);font-size:11px;letter-spacing:0.18em;color:var(--mute);">${escapeHtml(level)} · ${steps.length} steps · 約 ${totalWeeks} 週</p>
    <p style="margin:0 0 24px;color:var(--dim);font-size:13px;line-height:1.85;">${escapeHtml(data.overview || '')}</p>

    ${steps
      .map((s) => {
        const amazonUrl = `https://www.amazon.co.jp/s?k=${encodeURIComponent(s.title + ' ' + (s.author || ''))}`;
        const koshoUrl = `https://www.kosho.or.jp/products/list.php?search_word=${encodeURIComponent(s.title + ' ' + (s.author || ''))}`;
        return `
        <article class="result-card">
          <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">step ${s.order || '?'} · ${s.weeks || '?'} 週</p>
          <h3 class="result-card__title">${escapeHtml(s.title)}</h3>
          <p class="result-card__meta">${escapeHtml(s.author || '')}</p>
          <p class="result-card__body"><strong>位置付け:</strong> ${escapeHtml(s.purpose || '')}</p>
          <p class="result-card__body" style="margin-top:6px;"><strong>重点:</strong> ${escapeHtml(s.focus || '')}</p>
          <div class="result-card__chips" style="margin-top:12px;">
            <a class="chip" href="${escapeHtml(amazonUrl)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">Amazon</a>
            <a class="chip" href="${escapeHtml(koshoUrl)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">日本の古本屋</a>
          </div>
        </article>
      `;
      })
      .join('')}

    ${data.afterwards ? `<p style="margin-top:24px;padding:16px;border-left:2px solid var(--ink);font-family:var(--font-serif);line-height:1.85;background:var(--paper);">修了後の発展: ${escapeHtml(data.afterwards)}</p>` : ''}

    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>別の構成で設計し直す</span>
    </button>
  `;
  const screen = showScreen('reading-course-result', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', async () => {
    await runCourse(theme, level, count, books);
  });
}
